import React, { useRef } from 'react';
import { Link } from 'react-router';
import { Points } from 'components/Points/Points';
import { NoPrice } from 'components/NoPrice/NoPrice';
import { SeoCardRow } from 'store/services/seo/publicSeoApi';
import { CARD_BACK_DATA_URI } from 'utils/cardImageFallback';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { hasRealPrice } from 'utils/price';

/** Card detail URL for a ranked row: the same /cards/{slug}/{set}/{number} the rest of the site links to. */
export const rankedCardHref = (row: SeoCardRow): string =>
  buildCardUrl({ name: row.name, setCode: row.setCode, collectorNumber: row.collectorNumber });

/**
 * A 40x56 card thumbnail with the inline card-back fallback (never a network
 * fallback). Shared by the ranked rows and the /formats index previews.
 */
export const RankedCardThumb: React.FC<{ row: SeoCardRow; className?: string }> = ({ row, className }) => {
  // Fallback state lives in a ref, never in DOM dataset (re-renders reset it).
  const failedRef = useRef(false);
  return (
    <img
      className={`ranked-card-thumb${className ? ` ${className}` : ''}`}
      src={row.imageUrl || CARD_BACK_DATA_URI}
      alt=""
      width={40}
      height={56}
      loading="lazy"
      onError={(e) => {
        if (failedRef.current) return;
        failedRef.current = true;
        e.currentTarget.src = CARD_BACK_DATA_URI;
      }}
    />
  );
};

interface RankedCardRowProps {
  rank: number;
  row: SeoCardRow;
  /** Noun for the per-row count, e.g. "traders want it", "trades", "copies". */
  countLabel: (count: number) => string;
  hidden?: boolean;
}

/**
 * One ranked row (Asana 1219902842494504): rank, thumbnail, name, muted set
 * code and collector number, the Points pill (or NoPrice, never 0) and the
 * count label. The whole row links to the card detail page. Shared by the set
 * pages and the format pages.
 */
export const RankedCardRow: React.FC<RankedCardRowProps> = ({ rank, row, countLabel, hidden }) => (
  <li className="ranked-card-list__row" hidden={hidden} data-testid="ranked-card-row">
    <Link to={rankedCardHref(row)} className="ranked-card-list__link">
      <span className="ranked-card-list__rank">{rank}</span>
      <RankedCardThumb row={row} className="ranked-card-list__thumb" />
      <span className="ranked-card-list__text">
        <span className="ranked-card-list__name">{row.name}</span>
        <span className="ranked-card-list__printing">
          {row.setCode.toUpperCase()} #{row.collectorNumber}
        </span>
      </span>
      <span className="ranked-card-list__price">
        {hasRealPrice(row.pointsPrice) ? <Points points={row.pointsPrice} type="small" /> : <NoPrice />}
      </span>
      <span className="ranked-card-list__count">{countLabel(row.count)}</span>
    </Link>
  </li>
);

export default RankedCardRow;
